const SURVEY_DEVICE_ID_KEY = 'vxrSurveyDeviceId';
const FONT_CANDIDATES = [
    'Arial',
    'Arial Black',
    'Calibri',
    'Cambria',
    'Comic Sans MS',
    'Courier New',
    'Georgia',
    'Helvetica',
    'Menlo',
    'Monaco',
    'Roboto',
    'Tahoma',
    'Times New Roman',
    'Trebuchet MS',
    'Verdana',
];

function buildSurveyDeviceId(cryptoObject) {
    const randomValues = new Uint32Array(4);
    const browserCrypto = cryptoObject || (typeof window !== 'undefined' && (window.crypto || window.msCrypto));

    if (browserCrypto && browserCrypto.getRandomValues) {
        browserCrypto.getRandomValues(randomValues);
        return `survey-${Array.prototype.map.call(randomValues, value => value.toString(16)).join('-')}`;
    }

    return `survey-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function getOrCreateSurveyDeviceId(storage, cryptoObject) {
    try {
        const targetStorage = storage || (typeof window !== 'undefined' && window.localStorage);
        const existingDeviceId = targetStorage && targetStorage.getItem(SURVEY_DEVICE_ID_KEY);
        if (existingDeviceId) {
            return existingDeviceId;
        }

        const deviceId = buildSurveyDeviceId(cryptoObject);
        if (targetStorage && targetStorage.setItem) {
            targetStorage.setItem(SURVEY_DEVICE_ID_KEY, deviceId);
        }

        return deviceId;
    } catch (error) {
        return buildSurveyDeviceId(cryptoObject);
    }
}

function getRuntimeWindow(win) {
    return win || (typeof window !== 'undefined' ? window : {});
}

function getRuntimeNavigator(nav, win) {
    return nav || win.navigator || (typeof navigator !== 'undefined' ? navigator : {});
}

function detectFonts(win) {
    if (!win.document || !win.document.body) {
        return [];
    }

    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const defaultDimensions = {};
    const detectedFonts = [];
    const span = win.document.createElement('span');

    span.style.fontSize = testSize;
    span.style.position = 'absolute';
    span.style.left = '-9999px';
    span.style.top = '-9999px';
    span.style.visibility = 'hidden';
    span.innerHTML = testString;

    baseFonts.forEach(baseFont => {
        span.style.fontFamily = baseFont;
        win.document.body.appendChild(span);
        defaultDimensions[baseFont] = {
            width: span.offsetWidth,
            height: span.offsetHeight,
        };
        win.document.body.removeChild(span);
    });

    FONT_CANDIDATES.forEach(fontName => {
        const isDetected = baseFonts.some(baseFont => {
            span.style.fontFamily = `'${fontName}',${baseFont}`;
            win.document.body.appendChild(span);
            const matched = span.offsetWidth !== defaultDimensions[baseFont].width ||
                span.offsetHeight !== defaultDimensions[baseFont].height;
            win.document.body.removeChild(span);

            return matched;
        });

        if (isDetected) {
            detectedFonts.push(fontName);
        }
    });

    return detectedFonts;
}

function getWebGLContext(win) {
    if (!win.document) {
        return null;
    }

    const canvas = win.document.createElement('canvas');
    return canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
}

function getGpuInfo(win) {
    try {
        const gl = getWebGLContext(win);
        if (!gl) {
            return null;
        }

        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
            return {
                vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
                renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
            };
        }

        return {
            vendor: gl.getParameter(gl.VENDOR),
            renderer: gl.getParameter(gl.RENDERER),
        };
    } catch (error) {
        return null;
    }
}

function getWebGLSubPixelNoise(win) {
    try {
        const gl = getWebGLContext(win);
        if (!gl) {
            return '';
        }

        const pixels = new Uint8Array(4);
        gl.clearColor(0.123, 0.456, 0.789, 1);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixels);

        return Array.prototype.map.call(pixels, value => value.toString(16)).join('');
    } catch (error) {
        return '';
    }
}

function getClockDrift(win) {
    if (!win.performance || !win.performance.timeOrigin || !win.performance.now || !win.Date || !win.Date.now) {
        return null;
    }

    return Math.round(win.Date.now() - (win.performance.timeOrigin + win.performance.now()));
}

function getTimezone(win) {
    try {
        return win.Intl && win.Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
        return '';
    }
}

function collectSurveyFingerprint(overrides = {}) {
    const win = getRuntimeWindow(overrides.win);
    const nav = getRuntimeNavigator(overrides.nav, win);
    const screenInfo = win.screen || {};
    const fontDetector = overrides.detectFonts || detectFonts;
    const gpuDetector = overrides.getGpuInfo || getGpuInfo;
    const webglNoiseDetector = overrides.getWebGLSubPixelNoise || getWebGLSubPixelNoise;

    return {
        hardware: {
            gpu: gpuDetector(win),
            cpuCores: nav.hardwareConcurrency || null,
            screen: {
                width: screenInfo.width || null,
                height: screenInfo.height || null,
                colorDepth: screenInfo.colorDepth || null,
                pixelRatio: win.devicePixelRatio || null,
            },
        },
        softwareEnvironment: {
            fontList: fontDetector(win),
            language: nav.language || null,
            languages: nav.languages || [],
            platform: nav.platform || null,
            timezone: getTimezone(win) || null,
            touchPoints: nav.maxTouchPoints || 0,
        },
        physicalDeviations: {
            clockDrift: getClockDrift(win),
            webglMicroNoise: webglNoiseDetector(win),
        },
    };
}

function appendEncodedFingerprintPart(parts, key, value) {
    if (value === null || value === undefined || value === '') {
        return;
    }

    if (Array.isArray(value)) {
        value.forEach(item => appendEncodedFingerprintPart(parts, key, item));
        return;
    }

    if (typeof value === 'object') {
        Object.keys(value).forEach(childKey => {
            appendEncodedFingerprintPart(parts, `${key}.${childKey}`, value[childKey]);
        });
        return;
    }

    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
}

function encodeSurveyFingerprint(fingerprint) {
    const parts = [];

    Object.keys(fingerprint || {}).forEach(key => {
        appendEncodedFingerprintPart(parts, key, fingerprint[key]);
    });

    return parts.join('&');
}

function getBase64Encoder() {
    if (typeof window !== 'undefined' && window.btoa) {
        return window.btoa.bind(window);
    }

    if (typeof btoa !== 'undefined') {
        return btoa;
    }

    return null;
}

function encodeSurveyFingerprintForTransport(fingerprint, base64Encoder) {
    const encodedFingerprint = encodeSurveyFingerprint(fingerprint);
    const encoder = base64Encoder || getBase64Encoder();

    if (!encodedFingerprint || !encoder) {
        return '';
    }

    return encoder(encodedFingerprint);
}

function buildSurveyMetadataPayload(options = {}) {
    const payload = {};
    const transportFingerprint = encodeSurveyFingerprintForTransport(options.fingerprint, options.base64Encoder);

    if (options.deviceId) {
        payload.deviceId = options.deviceId;
    }

    if (transportFingerprint) {
        payload.fingerprint = transportFingerprint;
    }

    return payload;
}

export {
    SURVEY_DEVICE_ID_KEY,
    buildSurveyMetadataPayload,
    collectSurveyFingerprint,
    encodeSurveyFingerprint,
    encodeSurveyFingerprintForTransport,
    getOrCreateSurveyDeviceId,
};
